import React, {useEffect, useState} from 'react';
import {makeStyles} from '@mui/styles';
import {API_URL} from '../constants';
import {securedFetchRequest} from '@bidmii/common/lib/util/FetchRequest';
import {isVideo, renderVideo} from '../util/VideoHelpers';
import ImageGallery from 'react-image-gallery';
import AlertDialog from './dialogs/alertDialog';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import {AddPhotoAlternateOutlined, DeleteOutline} from '@mui/icons-material';
import {theme} from '../Theme';
import BidmiiIconButton from './StyledComponents/IconButton';
import BidmiiTooltip from './StyledComponents/BidmiiTooltip';
import * as Sentry from '@sentry/react';
import 'react-image-gallery/styles/css/image-gallery.css';
const useStyles = (isFullScreen: $TSFixMe) =>
  makeStyles(() => ({
    emptyCarousel: {
      position: 'relative',
      border: `3px dotted ${theme.palette.gray.border}`,
      borderRadius: 10,
      height: '36vh',
    },
    ImageGallery: {
      position: 'relative',
      padding: isFullScreen ? 0 : 'initial',

      '& .image-gallery-image': {
        height: isFullScreen ? '100vh !important' : '36vh !important',
        width: '100%',
        objectFit: 'cover',
        borderRadius: isFullScreen ? 'initial' : 12,
      },
      '& .image-gallery-icon': {
        color: theme.palette.secondary.main,
        filter: 'drop-shadow(0px 0px 0px #242424)',
      },
      '& .image-gallery-svg': {
        height: '100px !important',
      },
      '& .image-gallery-left-nav': {
        paddingLeft: 3,
        paddingTop: 10,
        paddingBottom: 10,
      },
      '& .image-gallery-right-nav': {
        paddingRight: 3,
        paddingTop: 10,
        paddingBottom: 0,
      },
      '& .image-gallery-description': {
        marginLeft: 10,
        top: 8,
        left: 8,
        bottom: 'auto',
        borderRadius: 6,
        fontSize: 13,
        backgroundColor: theme.palette.primary.main,
        padding: '4px 6px',
        textTransform: 'uppercase',
        fontFamily: "'AeonikProReqular', sans-serif",
        // height: 34,
      },
      '& .image-gallery-index': {
        top: 8,
        right: 8,
        borderRadius: 6,
        fontSize: 13,
        backgroundColor: theme.palette.primary.main,
        padding: '4px 6px',
        textTransform: 'uppercase',
        fontFamily: "'AeonikProReqular', sans-serif",
      },
    },
  }));

export default function ThumbnailImageList(props: $TSFixMe) {
  const galleryRef = props.imageGalleryRef;
  const [isFullScreen, setIsFullScreen] = useState(false);
  const classes = useStyles(isFullScreen)();

  const [data, setData] = useState(props.data);
  const [closeWarningOpen, setCloseWarningOpen] = useState(false);

  useEffect(() => {
    if (data !== props.data) {
      setData(props.data);
    }
  }, [data, props.data]);

  const items =
    data?.map((img: $TSFixMe) => {
      const item = {
        original: img.url,
        description: img.description.replace(' image', ''),
      };
      if (isVideo(img.url)) {
        (item as $TSFixMe).renderItem = renderVideo(img, isFullScreen);
      }
      return item;
    }) ?? [];

  const handleRemoveImage = (crop: $TSFixMe) => {
    setCloseWarningOpen(false);
    props.setLoading(true);
    const selectedImageIndex = galleryRef.current.getCurrentIndex();
    const images = data.filter((d: $TSFixMe) =>
      ['before_image', 'after_image'].includes(d.key),
    );

    deleteImage(images[selectedImageIndex], crop);
  };

  const deleteImage = (imageToDelete: $TSFixMe, crop: $TSFixMe) => {
    if (!imageToDelete?.id) {
      return;
    }
    const url = `${API_URL}/attachments/${imageToDelete.id}`;
    securedFetchRequest(url, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
      },
    })
      .then((response) => response.text())
      .then((res) => {
        setTimeout(function () {
          if (crop) {
            props.setLoading(false);
            props.updateRec();
          } else {
            data.forEach((attachment: $TSFixMe, index: $TSFixMe) => {
              if (attachment.id === imageToDelete.id) {
                data.splice(index, 1);
                const newData = [...data];
                setData(newData);
              }
            });
          }
        }, 100);

        if (!crop) {
          props.setLoading(false);
          props.updateRec();
        }
      })
      .catch((err) => {
        process.env.NODE_ENV === 'development' && console.error(err);
        Sentry.withScope(function (scope) {
          scope.setTag('section', 'HorizontalThumbnailImage.tsx deleteImage()');
          Sentry.captureException(err);
        });
      });
  };

  const addImageButton = (
    <form noValidate autoComplete="off">
      <input
        accept="image/*, .mp4, .avi, .webm capture"
        id={props.inputId || 'image_upload'}
        type="file"
        multiple
        onChange={props.onImageCapture}
        style={{display: 'none'}}
        capture={'environment'}
      />
      <label
        htmlFor={props.inputId || 'image_upload'}
        style={{marginBottom: 0}}>
        <BidmiiIconButton
          component="span"
          size={40}
          color={theme.palette.primary.main}
          iconColor={theme.palette.secondary.main}
          border={`1px solid ${theme.palette.secondary.main}`}
          style={{marginRight: 10, marginBottom: 10}}>
          <BidmiiTooltip title="Add image(s)">
            <AddPhotoAlternateOutlined />
          </BidmiiTooltip>
        </BidmiiIconButton>
      </label>
    </form>
  );

  if (data?.length === 0) {
    return (
      <Box className={classes.emptyCarousel}>
        <div style={{position: 'absolute', bottom: 8, right: 8}}>
          {addImageButton}
        </div>
      </Box>
    );
  }

  return (
    <div className="App">
      <div className={classes.ImageGallery}>
        <ImageGallery
          ref={galleryRef}
          items={items}
          infinite={true}
          showIndex={true}
          showNav={window.innerWidth > 600}
          showThumbnails={false}
          showPlayButton={false}
          showFullscreenButton={false}
          startIndex={items.length - 1}
          onScreenChange={(bool) => setIsFullScreen(bool)}
          onClick={() => {
            try {
              galleryRef.current.fullScreen();
            } catch (e) {
              // eslint-disable-next-line no-alert
              alert('Sorry, something went wrong');
            }
          }}
        />
        {data?.length > 0 && (
          <Grid
            container
            justifyContent="space-between"
            style={{position: 'absolute', bottom: 8}}>
            <span>
              {!props.disableDelete && (
                <BidmiiIconButton
                  size={40}
                  color={theme.palette.primary.main}
                  iconColor={theme.palette.secondary.main}
                  border={`1px solid ${theme.palette.secondary.main}`}
                  onClick={() => {
                    setCloseWarningOpen(true);
                  }}
                  style={{marginLeft: 10, marginBottom: 10}}>
                  <BidmiiTooltip title="Delete this image">
                    <DeleteOutline />
                  </BidmiiTooltip>
                </BidmiiIconButton>
              )}
            </span>

            <span style={{display: 'flex', alignItems: 'center'}}>
              {addImageButton}
            </span>
          </Grid>
        )}
      </div>

      <AlertDialog
        deletion
        open={closeWarningOpen}
        title="Confirm Image Deletion"
        body="Are you sure you want to delete this photo?"
        confirm="DELETE"
        close="CANCEL"
        onConfirm={handleRemoveImage}
        onClose={() => setCloseWarningOpen(false)}
      />

      {/* {cropperOpen && cropperDialog} */}
    </div>
  );
}
